import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTrash,
  faEdit,
  faUpload,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import {
  Modal,
  Box,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import { toast } from "react-hot-toast";

interface CompanySerial {
  id: string;
  itemCode: string;
  itemId: string;
  serial: string;
  serialStatus: string;
}

const CompanySerials: React.FC = () => {
  const [rows, setRows] = useState<CompanySerial[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<CompanySerial | null>(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedMaterialCode, setSelectedMaterialCode] = useState<string>("");
  const [materialSerials, setMaterialSerials] = useState<CompanySerial[]>([]);

  const fetchCompanySerials = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "CompanySerials"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);

      const result: CompanySerial[] = [];
      snap.forEach((docSnap) => {
        result.push({
          id: docSnap.id,
          ...(docSnap.data() as Omit<CompanySerial, "id">),
        });
      });

      setRows(result);
    } catch (error) {
      console.error("Error fetching CompanySerials:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanySerials();
  }, []);

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      return (
        (row.itemCode ?? "").toLowerCase().includes(q) ||
        (row.itemId ?? "").toLowerCase().includes(q) ||
        (row.serial ?? "").toLowerCase().includes(q) ||
        (row.serialStatus ?? "").toLowerCase().includes(q)
      );
    });
  }, [rows, searchQuery]);

  const uniqueRowsByItemCode = useMemo(() => {
    const map = new Map<string, CompanySerial>();

    for (const row of filteredRows) {
      const key = row.itemCode || row.itemId || row.id;
      if (!map.has(key)) {
        map.set(key, row);
      }
    }

    return Array.from(map.values());
  }, [filteredRows]);

  // const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = event.target.files?.[0];
  //   if (!file) return;

  //   try {
  //     const data = await file.arrayBuffer();
  //     const workbook = XLSX.read(data);
  //     const sheetName = workbook.SheetNames[0];
  //     const worksheet = workbook.Sheets[sheetName];
  //     const jsonData = XLSX.utils.sheet_to_json<any>(worksheet);

  //     let addedCount = 0;

  //     for (const row of jsonData) {
  //       const itemCode = String(row["item code"] ?? row["itemCode"] ?? "").trim();
  //       const itemId = String(row["item id"] ?? row["itemId"] ?? "").trim();
  //       const serial = String(row["serial"] ?? row["Serial"] ?? "").trim();

  //       if (!itemCode && !itemId && !serial) continue;
  //       if (!serial) continue;

  //       await addDoc(collection(db, "CompanySerials"), {
  //         itemCode,
  //         itemId,
  //         serial,
  //         serialStatus: "pending",
  //         createdAt: serverTimestamp(),
  //         updatedAt: serverTimestamp(),
  //       });

  //       addedCount++;
  //     }

  //     toast.success(`تم رفع ${addedCount} سيريال بنجاح`);
  //     fetchCompanySerials();
  //     event.target.value = "";
  //   } catch (error) {
  //     console.error("Excel upload error:", error);
  //     toast.error("حدث خطأ أثناء رفع ملف الإكسل");
  //   }
  // };


  const handleExcelUpload = async (
  event: React.ChangeEvent<HTMLInputElement>
) => {
  const file = event.target.files?.[0];

  if (!file) return;

  try {
    const data = await file.arrayBuffer();

    const workbook = XLSX.read(data);

    const sheetName = workbook.SheetNames[0];

    const worksheet = workbook.Sheets[sheetName];

    const jsonData = XLSX.utils.sheet_to_json<any>(worksheet);

    // جلب السيريلات الحالية مرة واحدة فقط
    const existingSnapshot = await getDocs(
      collection(db, "CompanySerials")
    );

    // تخزين السيريلات داخل Set
    const existingSerials = new Set<string>();

    existingSnapshot.forEach((docSnap) => {
      const serial = docSnap.data()?.serial;

      if (serial) {
        existingSerials.add(String(serial).trim());
      }
    });

    let addedCount = 0;
    let duplicateCount = 0;

    // تجهيز الإضافات
    const addPromises = [];

    for (const row of jsonData) {
      const itemCode = String(
        row["item code"] ?? row["itemCode"] ?? ""
      ).trim();

      const itemId = String(
        row["item id"] ?? row["itemId"] ?? ""
      ).trim();

      const serial = String(
        row["serial"] ?? row["Serial"] ?? ""
      ).trim();

      if (!serial) continue;

      // إذا موجود مسبقاً تجاهله
      if (existingSerials.has(serial)) {
        duplicateCount++;
        continue;
      }

      // إضافته للـ Set حتى يمنع تكراره داخل نفس الملف أيضاً
      existingSerials.add(serial);

      addPromises.push(
        addDoc(collection(db, "CompanySerials"), {
          itemCode,
          itemId,
          serial,
          serialStatus: "pending",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      );

      addedCount++;
    }

    // تنفيذ الإضافات دفعة واحدة
    await Promise.all(addPromises);

    toast.success(
      `تم رفع ${addedCount} سيريال جديد وتجاهل ${duplicateCount} سيريال مكرر`
    );

    fetchCompanySerials();

    event.target.value = "";
  } catch (error) {
    console.error("Excel upload error:", error);

    toast.error("حدث خطأ أثناء رفع ملف الإكسل");
  }
};
  const handleOpenEdit = (row: CompanySerial) => {
    setSelectedRow(row);
    setOpenEditModal(true);
  };

  const handleCloseEdit = () => {
    setSelectedRow(null);
    setOpenEditModal(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedRow) return;

    try {
      await updateDoc(doc(db, "CompanySerials", selectedRow.id), {
        itemCode: selectedRow.itemCode,
        itemId: selectedRow.itemId,
        serial: selectedRow.serial,
        serialStatus: selectedRow.serialStatus,
        updatedAt: serverTimestamp(),
      });

      toast.success("تم تعديل السيريال بنجاح");
      handleCloseEdit();
      fetchCompanySerials();
    } catch (error) {
      console.error("Edit error:", error);
      toast.error("حدث خطأ أثناء تعديل السيريال");
    }
  };

  const handleOpenDelete = (id: string) => {
    setDeleteId(id);
    setOpenDeleteModal(true);
  };

  const handleCloseDelete = () => {
    setDeleteId(null);
    setOpenDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await deleteDoc(doc(db, "CompanySerials", deleteId));
      toast.success("تم حذف السيريال بنجاح");
      handleCloseDelete();
      fetchCompanySerials();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("حدث خطأ أثناء حذف السيريال");
    }
  };

  const handleViewMaterialSerials = (itemCode: string) => {
    const list = rows.filter((row) => row.itemCode === itemCode);
    setSelectedMaterialCode(itemCode);
    setMaterialSerials(list);
    setOpenViewModal(true);
  };

  const handleCloseView = () => {
    setSelectedMaterialCode("");
    setMaterialSerials([]);
    setOpenViewModal(false);
  };

  return (
    <div
      className="w-full max-w-none mx-auto bg-white rounded-xl shadow-lg"
      style={{ width: 1400, direction: "rtl" }}
    >
      <div className="max-w-full flex items-center justify-between bg-[#262d34] text-white px-6 py-3 rounded-t-lg shadow-md">
        <h1
          className="text-xl font-bold flex-1 text-right"
          style={{ fontFamily: "Tajawal, sans-serif" }}
        >
          سيريلات الشركة
        </h1>

        <div className="flex items-center gap-3">
          <label>
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleExcelUpload}
            />
            <Button
              variant="contained"
              component="span"
              sx={{
                fontFamily: "Tajawal, sans-serif",
                backgroundColor: "#0f766e",
              }}
              startIcon={<FontAwesomeIcon icon={faUpload} />}
            >
              رفع Excel
            </Button>
          </label>

          <div className="flex items-center bg-gray-100 rounded-full px-4 py-1 w-80 shadow-md">
            <FontAwesomeIcon icon={faSearch} className="text-gray-500 ml-2" />
            <input
              type="text"
              placeholder="البحث بالكود أو رقم المادة أو السيريال أو الحالة..."
              className="bg-transparent focus:outline-none w-full text-sm text-gray-700 py-1"
              style={{ fontFamily: "Tajawal, sans-serif", textAlign: "right" }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

<div
  style={{
    display: "flex",
    justifyContent: "flex-start",
    padding: "16px 24px",
    fontFamily: "Tajawal, sans-serif",
  }}
>
  <div
    style={{
      background: "#f1f5f9",
      border: "1px solid #e2e8f0",
      borderRadius: "12px",
      padding: "14px 22px",
      minWidth: "220px",
      textAlign: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    }}
  >
    <div style={{ fontSize: 14, color: "#64748b", marginBottom: 6 }}>
      مجموع السيريلات في النظام
    </div>

    <div style={{ fontSize: 28, fontWeight: "bold", color: "#0f766e" }}>
      {rows.length}
    </div>
  </div>
</div>
      {loading ? (
        <p style={{ padding: 20, fontFamily: "Tajawal, sans-serif" }}>
          جاري التحميل...
        </p>
      ) : (
        <div className="table-container" style={{ overflowX: "auto" }}>
          <table className="product-table" style={{ width: "100%" }}>
            <thead className="color-table">
              <tr>
                <th>#</th>
                <th>Item Code</th>
                <th>Item ID</th>
                <th>عدد السيريلات</th>
                <th>الحالة</th>
                <th>الإجراءات</th>
              </tr>
            </thead>

            <tbody>
              {uniqueRowsByItemCode.map((row, index) => {
                const countForItem = rows.filter(
                  (x) => x.itemCode === row.itemCode
                ).length;

                return (
                  <tr key={row.id}>
                    <td>{index + 1}</td>
                    <td style={{ fontSize: 15 }}>{row.itemCode || "-"}</td>
                    <td style={{ fontSize: 15 }}>{row.itemId || "-"}</td>
                    <td style={{ fontSize: 15 }}>{countForItem}</td>
                    <td style={{ fontSize: 15 }}>{row.serialStatus || "-"}</td>

                    <td>
                      <div className="icon-container">
                        <button
                          className="icon-button"
                          onClick={() => handleViewMaterialSerials(row.itemCode)}
                          title="عرض السيريلات"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {uniqueRowsByItemCode.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 20 }}>
                    لا توجد بيانات
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={openViewModal} onClose={handleCloseView}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 900,
            maxHeight: "85vh",
            overflowY: "auto",
            bgcolor: "white",
            borderRadius: "12px",
            boxShadow: 24,
            p: 3,
            direction: "rtl",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              mb: 2,
              fontFamily: "Tajawal, sans-serif",
              fontWeight: "bold",
            }}
          >
            سيريلات المادة
          </Typography>

          <div style={{ marginBottom: 16, textAlign: "center" }}>
            <strong>Item Code:</strong> {selectedMaterialCode || "-"} &nbsp; | &nbsp;
            <strong>العدد:</strong> {materialSerials.length}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f3f4f6" }}>
                <th style={cellHead}>#</th>
                <th style={cellHead}>Item Code</th>
                <th style={cellHead}>Item ID</th>
                <th style={cellHead}>Serial</th>
                <th style={cellHead}>Status</th>
                <th style={cellHead}>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {materialSerials.map((serialRow, index) => (
                <tr key={serialRow.id}>
                  <td style={cellBody}>{index + 1}</td>
                  <td style={cellBody}>{serialRow.itemCode}</td>
                  <td style={cellBody}>{serialRow.itemId}</td>
                  <td style={cellBody}>{serialRow.serial}</td>
                  <td style={cellBody}>{serialRow.serialStatus}</td>
                  <td style={cellBody}>
                    <div className="icon-container" style={{ justifyContent: "center" }}>
                      <button
                        className="icon-button"
                        onClick={() => handleOpenEdit(serialRow)}
                        title="تعديل"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleOpenDelete(serialRow.id)}
                        title="حذف"
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          style={{ color: "red" }}
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {materialSerials.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 20 }}>
                    لا توجد سيريلات لهذه المادة
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleCloseView}
              sx={{ fontFamily: "Tajawal, sans-serif" }}
            >
              إغلاق
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal open={openEditModal} onClose={handleCloseEdit}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "white",
            borderRadius: "12px",
            boxShadow: 24,
            p: 3,
            direction: "rtl",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, textAlign: "center", fontFamily: "Tajawal, sans-serif" }}
          >
            تعديل السيريال
          </Typography>

          <div style={{ display: "grid", gap: 16 }}>
            <TextField
              fullWidth
              label="Item Code"
              value={selectedRow?.itemCode || ""}
              onChange={(e) =>
                setSelectedRow((prev) =>
                  prev ? { ...prev, itemCode: e.target.value } : prev
                )
              }
            />
            <TextField
              fullWidth
              label="Item ID"
              value={selectedRow?.itemId || ""}
              onChange={(e) =>
                setSelectedRow((prev) =>
                  prev ? { ...prev, itemId: e.target.value } : prev
                )
              }
            />
            <TextField
              fullWidth
              label="Serial"
              value={selectedRow?.serial || ""}
              onChange={(e) =>
                setSelectedRow((prev) =>
                  prev ? { ...prev, serial: e.target.value } : prev
                )
              }
            />
            <TextField
              fullWidth
              label="Serial Status"
              value={selectedRow?.serialStatus || ""}
              onChange={(e) =>
                setSelectedRow((prev) =>
                  prev ? { ...prev, serialStatus: e.target.value } : prev
                )
              }
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              marginTop: 20,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              sx={{ fontFamily: "Tajawal, sans-serif" }}
            >
              حفظ
            </Button>
            <Button
              variant="outlined"
              onClick={handleCloseEdit}
              sx={{ fontFamily: "Tajawal, sans-serif" }}
            >
              إلغاء
            </Button>
          </div>
        </Box>
      </Modal>

      <Modal open={openDeleteModal} onClose={handleCloseDelete}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "white",
            borderRadius: "12px",
            boxShadow: 24,
            p: 3,
            direction: "rtl",
          }}
        >
          <Typography
            variant="h6"
            sx={{ textAlign: "center", mb: 2, fontFamily: "Tajawal, sans-serif" }}
          >
            تأكيد الحذف
          </Typography>

          <Typography sx={{ textAlign: "center", mb: 3 }}>
            هل أنت متأكد من حذف هذا السيريال؟
          </Typography>

          <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              sx={{ fontFamily: "Tajawal, sans-serif" }}
            >
              حذف
            </Button>
            <Button
              variant="outlined"
              onClick={handleCloseDelete}
              sx={{ fontFamily: "Tajawal, sans-serif" }}
            >
              إلغاء
            </Button>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

const cellHead: React.CSSProperties = {
  border: "1px solid #ddd",
  padding: "10px",
  textAlign: "center",
  fontWeight: "bold",
};

const cellBody: React.CSSProperties = {
  border: "1px solid #ddd",
  padding: "10px",
  textAlign: "center",
};

export default CompanySerials;